import * as SliderPrimitive from "@radix-ui/react-slider";
import { Star } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { DEFAULT_SLIDER_CONFIG } from "@/components/PowerBlock/Blocks/categories/data-sources/HumanInputBlock/constants";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  getChoiceOptions,
  getRatingMax,
  getSliderBounds,
  getSliderInputValue,
  HumanUIComponentType,
} from "@/routes/smart-table/utils/humanInput";
import type { HumanConfiguration } from "@/types/evaluate";

interface HumanInputCellPreviewProps {
  config: HumanConfiguration;
  value: string | number | null;
  isDisabled?: boolean;
  onSave: (value: string | number) => Promise<void>;
}

const toFiniteNumber = (value: string | number | null | undefined, fallback: number): number => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const HUMAN_INPUT_AUTOSAVE_DELAY_MS = 450;
const MAX_INLINE_RATING_STARS = 5;

const HumanInputCellPreview = ({
  config,
  value,
  isDisabled = false,
  onSave,
}: HumanInputCellPreviewProps) => {
  const [draftValue, setDraftValue] = useState<string | number | null>(value);
  const draftValueRef = useRef<string | number | null>(value);
  const valueRef = useRef<string | number | null>(value);
  const lastSubmittedValueRef = useRef<string | number | null>(value);
  const queuedValueRef = useRef<string | number | null>(null);
  const saveInFlightRef = useRef(false);
  const onSaveRef = useRef(onSave);
  const isDisabledRef = useRef(isDisabled);
  const submitValueRef = useRef<(value: string | number | null) => void>(() => undefined);
  const uiComponent = config.ui_element?.component;

  valueRef.current = value;
  onSaveRef.current = onSave;
  isDisabledRef.current = isDisabled;

  const updateDraftValue = useCallback((nextValue: string | number | null) => {
    draftValueRef.current = nextValue;
    setDraftValue(nextValue);
  }, []);

  const submitValue = useCallback((nextValue: string | number | null) => {
    if (isDisabledRef.current || nextValue === null) {
      return;
    }

    if (saveInFlightRef.current) {
      queuedValueRef.current = nextValue === lastSubmittedValueRef.current ? null : nextValue;
      return;
    }

    if (nextValue === valueRef.current || nextValue === lastSubmittedValueRef.current) {
      return;
    }

    saveInFlightRef.current = true;
    lastSubmittedValueRef.current = nextValue;

    void onSaveRef
      .current(nextValue)
      .catch(() => {
        lastSubmittedValueRef.current = valueRef.current;
      })
      .finally(() => {
        saveInFlightRef.current = false;
        const queuedValue = queuedValueRef.current;
        queuedValueRef.current = null;

        if (queuedValue !== null) {
          submitValueRef.current(queuedValue);
        }
      });
  }, []);
  submitValueRef.current = submitValue;

  useEffect(() => {
    updateDraftValue(value);
    if (!saveInFlightRef.current) {
      lastSubmittedValueRef.current = value;
    }
  }, [updateDraftValue, value]);

  useEffect(
    () => () => {
      const pendingValue = draftValueRef.current;
      if (
        isDisabledRef.current ||
        pendingValue === null ||
        pendingValue === valueRef.current ||
        pendingValue === lastSubmittedValueRef.current
      ) {
        return;
      }

      submitValueRef.current(pendingValue);
    },
    [],
  );

  useEffect(() => {
    if (
      draftValue === null ||
      draftValue === value ||
      draftValue === lastSubmittedValueRef.current ||
      isDisabled
    ) {
      return;
    }

    const saveTimer = window.setTimeout(() => {
      submitValue(draftValue);
    }, HUMAN_INPUT_AUTOSAVE_DELAY_MS);

    return () => window.clearTimeout(saveTimer);
  }, [draftValue, isDisabled, submitValue, value]);

  const renderInput = () => {
    switch (uiComponent) {
      case HumanUIComponentType.SLIDER: {
        const { min, max } = getSliderBounds(config);
        const sliderValue = clamp(
          toFiniteNumber(getSliderInputValue(config, draftValue), min),
          min,
          max,
        );
        const inputValue = draftValue ?? "";
        const hasValue = draftValue !== null && draftValue !== undefined && draftValue !== "";
        const isBinary = min === 0 && max === 1;
        const getRoundedSliderValue = (nextValue: number[]): number | null => {
          const [nextSliderValue] = nextValue;
          return nextSliderValue === undefined ? null : Math.round(nextSliderValue);
        };

        if (isBinary) {
          return (
            <div
              role="group"
              aria-label="Binary human score"
              className="grid h-7 w-full grid-cols-2 rounded-md border border-border/60 bg-muted/20 p-0.5"
            >
              {[0, 1].map(optionValue => {
                const isSelected = hasValue && Number(draftValue) === optionValue;
                return (
                  <button
                    key={optionValue}
                    type="button"
                    aria-pressed={isSelected}
                    aria-label={`Set human score to ${optionValue}`}
                    disabled={isDisabled}
                    className={cn(
                      "rounded-sm text-xs font-medium tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-50",
                      isSelected
                        ? "bg-background text-foreground shadow-sm ring-1 ring-border/70"
                        : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                    )}
                    onClick={() => {
                      updateDraftValue(optionValue);
                      submitValue(optionValue);
                    }}
                  >
                    {optionValue}
                  </button>
                );
              })}
            </div>
          );
        }

        return (
          <div className="flex w-full min-w-0 items-center gap-2">
            <SliderPrimitive.Root
              className={cn(
                "relative flex h-5 min-w-0 flex-1 touch-none select-none items-center transition-opacity",
                !hasValue && "opacity-45",
              )}
              value={[sliderValue]}
              min={min}
              max={max}
              step={DEFAULT_SLIDER_CONFIG.step}
              disabled={isDisabled}
              aria-label={hasValue ? "Human input value" : "Human input value, not scored"}
              onValueChange={nextValue => {
                const nextSliderValue = getRoundedSliderValue(nextValue);
                if (nextSliderValue !== null) {
                  updateDraftValue(nextSliderValue);
                }
              }}
              onValueCommit={nextValue => {
                const nextSliderValue = getRoundedSliderValue(nextValue);
                if (nextSliderValue === null) {
                  return;
                }

                updateDraftValue(nextSliderValue);
                submitValue(nextSliderValue);
              }}
            >
              <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-slate-200/80">
                <SliderPrimitive.Range className="absolute h-full rounded-full bg-blue-500/80" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb className="block h-3.5 w-3.5 rounded-full border border-slate-300/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.14)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/25 disabled:pointer-events-none disabled:opacity-50" />
            </SliderPrimitive.Root>
            <Input
              value={inputValue}
              type="number"
              min={min}
              max={max}
              disabled={isDisabled}
              placeholder="—"
              className="h-6 w-12 shrink-0 rounded-md border-border/60 bg-background/80 px-1.5 text-center text-[11px] tabular-nums shadow-none focus-visible:ring-1 focus-visible:ring-blue-500/20"
              onChange={event => {
                const nextValue = event.target.value;
                updateDraftValue(nextValue === "" ? null : Number(nextValue));
              }}
              onBlur={() => {
                if (draftValue === null || draftValue === undefined || draftValue === "") {
                  return;
                }

                const clampedValue = clamp(toFiniteNumber(draftValue, min), min, max);
                updateDraftValue(clampedValue);
                submitValue(clampedValue);
              }}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitValue(draftValue);
                }

                if (event.key === "Escape") {
                  event.preventDefault();
                  updateDraftValue(value);
                }
              }}
            />
          </div>
        );
      }

      case HumanUIComponentType.RATING: {
        const max = getRatingMax(config);
        const ratingValue = clamp(toFiniteNumber(draftValue, 0), 0, max);

        if (max > MAX_INLINE_RATING_STARS) {
          return (
            <Select
              value={ratingValue ? String(ratingValue) : undefined}
              disabled={isDisabled}
              onValueChange={nextValue => {
                const numericValue = Number(nextValue);
                updateDraftValue(numericValue);
                submitValue(numericValue);
              }}
            >
              <SelectTrigger
                aria-label="Human rating"
                className="h-6 min-w-0 max-w-full truncate rounded-md border-border/60 bg-background/80 px-2 py-0 text-xs shadow-none focus:ring-1 focus:ring-blue-500/20 [&>span]:min-w-0 [&>span]:truncate"
              >
                <SelectValue placeholder="Select rating…" />
              </SelectTrigger>
              <SelectContent className="z-[1000] max-w-[min(var(--radix-select-trigger-width),calc(100vw-1rem))]">
                {Array.from({ length: max }, (_, index) => {
                  const optionValue = index + 1;
                  return (
                    <SelectItem key={optionValue} value={String(optionValue)}>
                      {optionValue} / {max}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          );
        }

        return (
          <div className="flex w-full min-w-0 items-center gap-0.5">
            {Array.from({ length: max }, (_, index) => {
              const optionValue = index + 1;
              const isSelected = optionValue <= ratingValue;

              return (
                <button
                  key={optionValue}
                  type="button"
                  disabled={isDisabled}
                  className="flex h-6 min-w-0 flex-1 items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={`Set rating to ${optionValue}`}
                  onClick={() => {
                    updateDraftValue(optionValue);
                    submitValue(optionValue);
                  }}
                >
                  <Star
                    className={cn(
                      "h-3.5 w-3.5 transition-colors",
                      isSelected
                        ? "fill-amber-400 text-amber-400"
                        : "fill-transparent text-slate-300 hover:text-slate-400",
                    )}
                  />
                </button>
              );
            })}
          </div>
        );
      }

      case HumanUIComponentType.CHOICE: {
        const options = getChoiceOptions(config);
        const selectedValue = typeof draftValue === "string" ? draftValue : "";

        return (
          <Select
            value={selectedValue || undefined}
            disabled={isDisabled}
            onValueChange={nextValue => {
              updateDraftValue(nextValue);
              submitValue(nextValue);
            }}
          >
            <SelectTrigger
              aria-label="Human input choice"
              className="h-6 min-w-0 max-w-full truncate rounded-md border-border/60 bg-background/80 px-2 py-0 text-xs shadow-none focus:ring-1 focus:ring-blue-500/20 [&>span]:min-w-0 [&>span]:truncate"
            >
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent className="z-[1000] max-w-[min(var(--radix-select-trigger-width),calc(100vw-1rem))]">
              {options.map(option => (
                <SelectItem key={option} value={option} className="max-w-full">
                  <span className="block max-w-full truncate">{option}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      default:
        return (
          <div className="min-w-0 truncate px-1 text-xs text-muted-foreground">
            Configure a slider input
          </div>
        );
    }
  };

  return (
    <div
      className="flex h-full w-full min-w-0 items-center overflow-hidden px-1.5 py-0.5"
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onPointerDown={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
    >
      <div className="min-w-0 flex-1 overflow-hidden">{renderInput()}</div>
    </div>
  );
};

export default HumanInputCellPreview;
